import * as XLSX from 'xlsx';

/**
 * Convert string to ArrayBuffer for Excel file generation
 * @param {string} s - String to convert
 * @returns {ArrayBuffer} - Converted ArrayBuffer
 */
const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
    return buf;
};

/**
 * Generate and download Excel file from JSON data
 * @param {Array} data - Array of objects to convert to Excel
 * @param {string} fileName - Name of the file to download
 * @param {Array} headers - Array of header objects with { header: string, key: string }
 * @returns {Promise<void>}
 */
export const generateAndDownloadExcel = async (data, fileName = 'export.xlsx', headers = []) => {
    try {
        // Create new workbook
        const workbook = XLSX.utils.book_new();

        // Create worksheet data with headers
        const worksheetData = [
            headers.map(h => h.header),
            ...data.map(item =>
                headers.map(h => {
                    // Handle different data types
                    const value = item[h.key];
                    if (value === null || value === undefined) return '';
                    if (typeof value === 'object') return JSON.stringify(value);
                    return String(value); // Convert all values to string
                })
            )
        ];

        // Convert to worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Apply styles to headers and data
        const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

        // Style for headers
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
            if (!worksheet[cellRef]) continue;
            worksheet[cellRef].s = {
                font: { bold: true, color: { rgb: "000000" } },
                alignment: { horizontal: "center" },
                fill: { fgColor: { rgb: "CCCCCC" } }
            };
        }

        // Style for data cells
        for (let R = 1; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
                if (!worksheet[cellRef]) continue;
                worksheet[cellRef].s = {
                    font: { color: { rgb: "000000" } },
                    alignment: { horizontal: "left" }
                };
            }
        }

        // Set column widths
        const colWidths = headers.map(() => ({ wch: 30 })); // Increased width for better readability
        worksheet['!cols'] = colWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        // Generate binary data
        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
        const blob = new Blob([s2ab(wbout)], { type: "application/octet-stream" });

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true, message: 'File downloaded successfully' };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Generate and download Equipment Check Excel
 * @param {Array} data - Equipment check data
 * @returns {Promise<void>}
 */
export const downloadEquipmentCheckExcel = async (data) => {
    const headers = [
        { header: "ID", key: "ID" },
        { header: "Equipment Type", key: "Equipment Type" },
        { header: "Maintenance Type", key: "Maintenance Type" },
        { header: "Maintenance Interval", key: "Maintenance Interval" },
        { header: "Unique Identifier", key: "Unique Identifier" },
        { header: "Maintenance Checklist", key: "Maintenance Checklist" },
        { header: "Photos Required", key: "Photos Required" },
        { header: "Expected Output", key: "Expected Output" },
        { header: "Portuguese", key: "Portuguese" },
        { header: "Brazilian", key: "Brazilian" },
    ];

    // No need to add IDs as they are already in the data
    return generateAndDownloadExcel(data, "EquipmentMaintenanceCheck.xlsx", headers);
};

export const downloadBufferAsExcel = (buffer, fileName) => {
  try {
    // Ensure we have a valid buffer
    if (!buffer || buffer.length === 0) {
      throw new Error('Invalid buffer data received');
    }
    
    // Create blob from buffer with proper Excel MIME type
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'File downloaded successfully' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const downloadBlobAsExcel = (blob, fileName) => {
  try {
    // Ensure we have a valid blob
    if (!blob || blob.size === 0) {
      throw new Error('Invalid blob data received');
    }
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'File downloaded successfully' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const downloadBufferAsFile = (buffer, fileName, mimeType = 'application/octet-stream') => {
  try {
    // Create blob from buffer
    const blob = new Blob([buffer], { type: mimeType });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'File downloaded successfully' };
  } catch (error) {
    return { success: false, error: error.message };
  }
}; 

export const handleExcelDownload = async (url, fileName) => {
  try {
    // Fetch the file from the URL
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Get the blob from the response
    const blob = await response.blob();
    
    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return { success: true, message: 'File downloaded successfully' };
  } catch (error) {
    return { success: false, error: error.message };
  }
}; 